// PayersTab.tsx — Admin → Billing & Insurance → Insurers & Schemes → Payers.
// The insurer/TPA/scheme/corporate organisation master
// (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.2). Picking one of these on a
// patient's coverage (Face Sheet → Payer / scheme) is what opens a claim — a
// bare scheme category alone never does, since a claim needs to know whom to
// bill. The list starts empty on purpose: real insurer names are never seeded
// in code.
//
// CSV import/export (design §4.2/§7, plan 1.2, wireframe S9) is the
// MasterCsvImportBar above the table: create-only, so an existing code is a
// row-level error and edits stay on screen (ETag + per-field audit).
//
// List + side drawer + ⋯ row menu (docs/design/admin-panel/Admin_Panel_Wireframe.html, D4).
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { MasterCsvImportBar } from './MasterCsvImportBar';
import { getPayers, createPayer, updatePayer, deletePayer } from '../api';
import type { Payer } from '../types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect } from '@/components/ui/Input';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { toast } from '@/hooks/useToast';
import { Dash, SettingsToolbar, filterByStatus, matchesSearch, type StatusFilter } from '../shared/SettingsToolbar';

const PAYER_TYPES = [
  { value: 'insurer', label: 'Insurer' },
  { value: 'tpa', label: 'TPA' },
  { value: 'govt_scheme', label: 'Govt. scheme' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'psu', label: 'PSU' },
  { value: 'other', label: 'Other' },
];
const typeLabel = (t: string) => PAYER_TYPES.find((x) => x.value === t)?.label ?? t;

type Draft = {
  payer_type: string; code: string; name: string; irdai_reg_no: string;
  claims_email: string; phone: string; is_network: boolean;
  tds_percent: string; requires_preauth: boolean; max_upload_mb: string;
};

const BLANK_DRAFT: Draft = {
  payer_type: 'insurer', code: '', name: '', irdai_reg_no: '', claims_email: '', phone: '',
  is_network: true, tds_percent: '10', requires_preauth: true, max_upload_mb: '',
};

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function PayersTab() {
  const [payers, setPayers] = useState<Payer[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<Payer | 'new' | null>(null);
  const [confirm, setConfirm] = useState<{ payer: Payer; kind: 'deactivate' | 'delete' } | null>(null);

  const load = useCallback(() => {
    getPayers({ active: 'all' }).then((p: Payer[]) => { setPayers(p); setLoadError(''); }).catch((e: unknown) => setLoadError(errText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ title: ok, variant: 'success' }); load(); }
    catch (e) { toast({ title: 'Not done', description: errText(e), variant: 'danger' }); }
  };

  const rows = useMemo(
    () => filterByStatus(payers ?? [], status).filter((p) => matchesSearch(search, p.code, p.name, typeLabel(p.payer_type), p.claims_email, p.phone)),
    [payers, status, search]
  );

  // Wireframe S9's dense Payers table (Code/Name/Type/Network/TDS) — MOU
  // expiry isn't a modelled field here, so Email/Phone fill that slot.
  const columns: DataTableColumn<Payer>[] = [
    { key: 'code', header: 'Code', widthClassName: 'w-[150px]', sortable: true, sortValue: (p) => p.code,
      render: (p) => <span className="font-mono text-[12px] font-semibold">{p.code}</span> },
    { key: 'name', header: 'Name', sortable: true, sortValue: (p) => p.name, render: (p) => <span className="font-semibold">{p.name}</span> },
    { key: 'type', header: 'Type', widthClassName: 'w-[120px]', sortable: true, sortValue: (p) => p.payer_type, render: (p) => typeLabel(p.payer_type) },
    { key: 'network', header: 'Network', widthClassName: 'w-[100px]',
      render: (p) => (p.is_network ? <Badge variant="info">Cashless</Badge> : <Dash />) },
    { key: 'contact', header: 'Contact', className: 'text-ink-muted', render: (p) => p.claims_email || p.phone || <Dash /> },
    { key: 'tds', header: 'TDS', align: 'right', widthClassName: 'w-[70px]', render: (p) => `${p.tds_percent}%` },
    { key: 'status', header: 'Status', widthClassName: 'w-[100px]',
      render: (p) => <Badge variant={p.active ? 'success' : 'neutral'}>{p.active ? 'Active' : 'Inactive'}</Badge> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', widthClassName: 'w-[56px]',
      render: (p) => (
        <RowActionsMenu
          label={`Actions for ${p.name}`}
          actions={[
            { label: 'Edit details', onSelect: () => setEditing(p) },
            p.active
              ? { label: 'Deactivate…', danger: true, onSelect: () => setConfirm({ payer: p, kind: 'deactivate' }) }
              : { label: 'Reactivate', onSelect: () => act(() => updatePayer(p.id, { active: true }), `${p.name} reactivated`) },
            { label: 'Delete…', danger: true, onSelect: () => setConfirm({ payer: p, kind: 'delete' }) },
          ]}
        />
      ) },
  ];

  return (
    <div className="fade-in">
      <MasterCsvImportBar master="payers" onImported={load} />

      <SettingsToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search payers"
        status={status}
        onStatus={setStatus}
        count={payers ? `${rows.length} payer${rows.length === 1 ? '' : 's'}` : undefined}
        action={<Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> Add payer</Button>}
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id}
        isLoading={!payers && !loadError}
        isError={!!loadError}
        errorMessage={loadError}
        onRetry={load}
        emptyLabel={search ? 'No payers match' : status === 'inactive' ? 'No inactive payers' : 'No payers yet'}
        emptyHint={!search && status !== 'inactive' ? 'Add each insurer, TPA or scheme this hospital deals with, or import them from CSV.' : undefined}
      />

      <PayerSheet
        payer={editing}
        onClose={() => setEditing(null)}
        onSaved={(msg) => { setEditing(null); toast({ title: msg, variant: 'success' }); load(); }}
      />

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.kind === 'delete' ? `Delete ${confirm.payer.name}?` : `Deactivate ${confirm?.payer.name ?? ''}?`}
        description={confirm?.kind === 'delete'
          ? 'This removes the payer permanently. If any coverage or claim already uses it, the delete is refused — deactivate it instead.'
          : 'It can no longer be picked for a new coverage. Existing coverages and claims keep it, and you can reactivate it later.'}
        confirmLabel={confirm?.kind === 'delete' ? 'Delete' : 'Deactivate'}
        onConfirm={() => {
          const c = confirm; if (!c) return;
          if (c.kind === 'delete') act(() => deletePayer(c.payer.id), `${c.payer.name} deleted`);
          else act(() => updatePayer(c.payer.id, { active: false }), `${c.payer.name} deactivated`);
        }}
      />
    </div>
  );
}

function PayerSheet({ payer, onClose, onSaved }: { payer: Payer | 'new' | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const isNew = payer === 'new';
  const [d, setD] = useState<Draft>(BLANK_DRAFT);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!payer) return;
    setError(''); setTouched(false);
    setD(payer === 'new' ? BLANK_DRAFT : {
      payer_type: payer.payer_type, code: payer.code, name: payer.name, irdai_reg_no: payer.irdai_reg_no ?? '',
      claims_email: payer.claims_email ?? '', phone: payer.phone ?? '', is_network: payer.is_network,
      tds_percent: String(payer.tds_percent), requires_preauth: payer.requires_preauth,
      max_upload_mb: payer.max_upload_mb != null ? String(Number(payer.max_upload_mb)) : '',
    });
  }, [payer]);

  const set = (k: keyof Draft) => (e: { target: { value: string } }) => setD((x) => ({ ...x, [k]: e.target.value }));

  const save = async () => {
    setTouched(true);
    if (!d.name.trim() || (isNew && !d.code.trim())) return;
    setSaving(true); setError('');
    const common = {
      payer_type: d.payer_type as Payer['payer_type'], name: d.name.trim(), is_network: d.is_network,
      requires_preauth: d.requires_preauth, max_upload_mb: d.max_upload_mb === '' ? null : Number(d.max_upload_mb),
    };
    try {
      if (isNew) {
        await createPayer({
          ...common, code: d.code.trim(),
          irdai_reg_no: d.irdai_reg_no.trim() || undefined, claims_email: d.claims_email.trim() || undefined,
          phone: d.phone.trim() || undefined, tds_percent: d.tds_percent === '' ? undefined : Number(d.tds_percent),
        });
        onSaved(`${common.name} added`);
      } else if (payer) {
        await updatePayer(payer.id, {
          ...common,
          irdai_reg_no: d.irdai_reg_no.trim() || null, claims_email: d.claims_email.trim() || null,
          phone: d.phone.trim() || null, tds_percent: Number(d.tds_percent),
        });
        onSaved(`${common.name} saved`);
      }
    } catch (e) { setError(errText(e)); }
    setSaving(false);
  };

  return (
    <Sheet open={!!payer} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{isNew ? 'Add payer' : `Edit ${d.name || 'payer'}`}</SheetTitle>
          <SheetDescription>An insurer, TPA, scheme or corporate that claims are billed to.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Type" required>
              <NativeSelect value={d.payer_type} onChange={set('payer_type')}>
                {PAYER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Code" required error={touched && isNew && !d.code.trim() ? 'Enter a code' : undefined}
              hint={isNew ? 'Can’t be changed later.' : undefined}>
              <Input value={d.code} onChange={set('code')} disabled={!isNew} placeholder="e.g. STAR-HEALTH" className="font-mono" />
            </FormField>
          </div>
          <FormField label="Name" required error={touched && !d.name.trim() ? 'Enter the payer’s name' : undefined}>
            <Input value={d.name} onChange={set('name')} placeholder="e.g. Star Health and Allied Insurance" autoFocus={!isNew} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="IRDAI registration no.">
              <Input value={d.irdai_reg_no} onChange={set('irdai_reg_no')} />
            </FormField>
            <FormField label="TDS %" hint="Deducted by the payer at settlement.">
              <Input type="number" min={0} max={100} value={d.tds_percent} onChange={set('tds_percent')} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Claims email">
              <Input type="email" value={d.claims_email} onChange={set('claims_email')} />
            </FormField>
            <FormField label="Phone">
              <Input value={d.phone} onChange={set('phone')} inputMode="tel" />
            </FormField>
          </div>
          <FormField label="Portal upload limit (MB per file)" hint="Claim packets larger than this are split into parts. Leave blank for no limit.">
            <Input type="number" min={0.5} step={0.5} value={d.max_upload_mb} placeholder="No limit" onChange={set('max_upload_mb')} />
          </FormField>
          <div className="flex flex-col gap-2.5">
            <label className="flex items-center gap-2 text-[13px]">
              <Checkbox checked={d.is_network} onCheckedChange={(v) => setD((x) => ({ ...x, is_network: !!v }))} />
              Network hospital (cashless-capable)
            </label>
            <label className="flex items-center gap-2 text-[13px]">
              <Checkbox checked={d.requires_preauth} onCheckedChange={(v) => setD((x) => ({ ...x, requires_preauth: !!v }))} />
              Requires pre-authorisation
            </label>
          </div>
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>{isNew ? 'Add payer' : 'Save changes'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
